/* eslint-disable react/no-unknown-property */
import React, { useEffect, useState, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

import EarthComponent from './EarthComponent';
import GroundStationMarker from './components/GroundStationMarker';
import SatelliteMarkers from './components/SatelliteMarkers';
import SatelliteModal from './components/SatelliteModal';
import { useGroundStations } from './hooks/useGroundStations';
import { useSatelliteSchedule } from './hooks/useSatelliteSchedule';
// import { Line } from '@react-three/drei';
import { TriangleDashedFlowLine } from './components/TriangleDashedFlowLine';
import PastGradientLine from './components/PastGradientLine';
import { twoline2satrec, propagate, gstime, eciToGeodetic, degreesLat, degreesLong } from 'satellite.js';
import dayjs from 'dayjs';

// 다운샘플링 함수 (성능 향상을 위해 포인트 수 줄이기)
function downsample<T>(arr: T[], step = 2) {
  if (step <= 1) return arr;
  const out: T[] = [];
  for (let i = 0; i < arr.length; i += step) out.push(arr[i]);
  if (arr.length && out[out.length - 1] !== arr[arr.length - 1]) out.push(arr[arr.length - 1]);
  return out;
}

// 좌표 변환 유틸리티 함수들 (SatelliteMarkers와 동일)
const toCartesian = (lat: number, lon: number, height: number) => {
  const latRad = (lat * Math.PI) / 180;
  const lonRad = (lon * Math.PI) / 180;
  const earthRadius = 6371;
  const radius = earthRadius + height;

  const x = radius * Math.cos(latRad) * Math.sin(lonRad);
  const y = radius * Math.sin(latRad);
  const z = radius * Math.cos(latRad) * Math.cos(lonRad);

  return { x, y, z };
};

const scaling = (x: number, y: number, z: number) => {
  const earthRadius = 6371;
  const scaleFactor = 10 / earthRadius;
  return {
    x: x * scaleFactor,
    y: y * scaleFactor,
    z: z * scaleFactor,
  };
};

// 위성별 고유한 핵사 색상 시스템
const getSatelliteColors = (baseIndex: number) => {
  const hexColors = [ 0x103466,  0x2046A1, 0x1477D2];
  const colorIndex = baseIndex % hexColors.length;
  const mainColor = hexColors[colorIndex];
  
  return {
    main: mainColor,
    bright: mainColor,
    medium: mainColor
  };
};



// 위성 궤도 라인 컴포넌트
const SatelliteOrbitLines = ({ latestSpacecrafts }: { latestSpacecrafts?: any[] }) => {
  const [orbitData, setOrbitData] = useState<{ 
    [key: string]: { 
      points: THREE.Vector3[],
      startEpochMs: number,
      timeStepSec: number,
    } 
  }>({});
  const [, setTick] = useState(0);
  const accRef = useRef(0);

  useEffect(() => {
    if (!latestSpacecrafts || latestSpacecrafts.length === 0) return;

    const calculateOrbits = () => {
      const newOrbitData: { 
        [key: string]: { 
          points: THREE.Vector3[],
          startEpochMs: number,
          timeStepSec: number,
        } 
      } = {};

      // NORAD ID 기준으로 중복 제거
      const uniqueSatellites = latestSpacecrafts.filter((satellite, index, self) => 
        index === self.findIndex(s => s.noradId === satellite.noradId)
      );

      uniqueSatellites.forEach((satellite, idx) => {
        try {
          if (!satellite.tleFirst || !satellite.tleSecond) return;

          // TLE를 satellite.js 객체로 변환
          const satrec = twoline2satrec(satellite.tleFirst, satellite.tleSecond);

          // 궤도 예측 기간 설정 - 현재 시간 기준으로 60분 과거 + 60분 미래
          const currentTime = dayjs(new Date());
          const startTime = currentTime.subtract(60, 'minute'); // 60분 전
          const futureTime = currentTime.add(60, 'minute'); // 60분 후
          
          // 궤도 계산 정밀도 설정
          const timeStep = 1; // 1초 간격
          
          // 전체 궤도 포인트 생성 (과거 + 미래)
          const allPoints: THREE.Vector3[] = [];

          // 전체 기간 (60분 전 ~ 60분 후) 계산
          for (let time = startTime; time.isBefore(futureTime); time = time.add(timeStep, 'second')) {
            try {
              const positionAndVelocity = propagate(satrec, time.toDate());
              const positionEci = positionAndVelocity.position;
              
              if (positionEci && typeof positionEci === 'object') {
                // 현재 시각 기준 그리니치 평균 항성시(GMST) 계산 → 지구 자전각 보정용
                const gmst = gstime(time.toDate());
                
                // ECI(우주 기준) 좌표 -> 지구 기준 지리 좌표계(위도, 경도, 고도)로 변환 (GMST 자전 보정 포함)
                const geodetic = eciToGeodetic(positionEci, gmst);
                const latitude = degreesLat(geodetic.latitude);     // 라디안 -> 위도(degrees)
                const longitude = degreesLong(geodetic.longitude);  // 라디안 -> 경도(degrees)
                const altitude = geodetic.height;                             // 고도(km)
                
                // SatelliteMarkers와 동일한 좌표 변환 및 스케일링
                const coords = toCartesian(latitude, longitude, altitude);
                const scaled = scaling(coords.x, coords.y, coords.z);
                
                const scaledPosition = new THREE.Vector3(scaled.x, scaled.y, scaled.z);
                allPoints.push(scaledPosition);
              }
            } catch (error) {
              console.warn(`위성 ${satellite.name} 위치 계산 실패:`, error);
            }
          }

          // noradId를 키로 사용하여 이름 충돌/중복 방지
          newOrbitData[String(satellite.noradId)] = {
            points: allPoints,
            startEpochMs: startTime.toDate().getTime(),
            timeStepSec: timeStep,
          };
        } catch (error) {
          console.error(`위성 ${satellite.name} 궤도 계산 실패:`, error);
        }
      });

      setOrbitData(newOrbitData);
    };

    calculateOrbits();
    
    // 1분마다 궤도 재계산하여 롤링 윈도우 유지 (60분 과거 + 60분 미래)
    const interval = setInterval(calculateOrbits, 60000); // 60초 = 1분
    
    return () => clearInterval(interval);
  }, [latestSpacecrafts]);

  // 매 프레임: 1초마다 tick 증가 → 리렌더 유도(분할 인덱스 전진)
  useFrame((_, delta) => {
    accRef.current += delta;
    if (accRef.current >= 1) {
      setTick(t => t + 1);
      accRef.current = 0;
    }
  });

  return (
    <>
      {Object.entries(orbitData).map(([noradIdKey, data], idx) => {
        // 위성별 고유한 선명한 색상 생성
        const satelliteColors = getSatelliteColors(idx);
        
        // 현재 인덱스 계산(동적): startEpochMs 기준으로 경과 시간 / timeStepSec
        const total = data.points.length;
        const elapsedSec = Math.max(0, Math.floor((Date.now() - data.startEpochMs) / (data.timeStepSec * 1000)));
        const currentIdx = Math.min(total - 1, elapsedSec);
        const past = data.points.slice(0, currentIdx);
        const future = data.points.slice(currentIdx);
        
        return (
          <React.Fragment key={noradIdKey}>
            {/* 과거 궤도 (실시간 경로) - 자연스러운 그라데이션 효과 (원본 hex 팔레트 기반) */}
            {past && past.length > 1 && (
              <PastGradientLine
                points={downsample(past, 2)}
                baseColor={satelliteColors.main}
                lineWidth={2.5}
                opacity={0.9}
                // 최근(더 연하게), 과거는 색감 유지
                lightOld={72}
                lightRecent={55}
                // 채도 높여 회색 느낌 제거
                satOld={55}
                satRecent={85}
                recentSpanRatio={0.25}
                frustumCulled={false}
              />
            )}
            
            {/* 미래 궤도 (예측 경로) - 점선 애니메이션 효과 */}
            {future && future.length > 0 && (
              <>
                {/* 미래 궤도: 점선이 위성 진행 방향으로 흐르는 애니메이션 */}
                {/**
                 * 기존 점선 애니메이션 주석 처리
                 * <AnimatedFlowLine
                 *   points={future.length > 1 ? downsample(future, 2) : future}
                 *   color={satelliteColors.bright}
                 *   lineWidth={4}
                 *   opacity={1}
                 *   speed={1.5}
                 *   dashSize={0.5}
                 *   dashGap={8}
                 *   frustumCulled={false}
                 * />
                 */}
                {future && future.length > 1 && (
                  <TriangleDashedFlowLine
                    points={downsample(future, 2)}
                    color={satelliteColors.bright}
                    opacity={1}
                    speed={0.4}
                    dashSize={0.5}
                    dashGap={0}
                    arrowCount={40} // 고정 화살표 개수
                    headLength={0.2}   // 기본보다 짧게
                    headWidth={0.2}   // 기본보다 좁게
                    lineWidth={0.5}    // 필요시 같이 조절
                    frustumCulled={false}
                  />
                )}
              </>
            )}


          </React.Fragment>
        );
      })}
    </>
  );
};







interface EarthGroupProps {
  latestSpacecrafts?: any[];
  onGroundStationSelect?: (stationName: string) => void;
  groundStations?: any[];
  selectedGroundStation?: string | number;
}

// 지구 그룹 컴포넌트
const EarthGroup = ({ 
  latestSpacecrafts, 
  onGroundStationSelect,
  groundStations,
  selectedGroundStation
}: EarthGroupProps) => {
  const [selectedSatellite, setSelectedSatellite] = useState<any>(null);

  // 커스텀 훅 사용
  const { localSelectedGroundStation } = useGroundStations({
    groundStations,
    selectedGroundStation,
    onGroundStationSelect
  });

  const { scheduleData, isLoading, fetchSatelliteSchedule } = useSatelliteSchedule();



  // 위성 클릭 핸들러
  const handleSatelliteClick = async (satellite: any) => {
    setSelectedSatellite(satellite);
    await fetchSatelliteSchedule(satellite);
  };



  return (
    <>
      {/* 지구 컴포넌트 */}
      <EarthComponent />
      
      {/* 선택된 지상국 표시 */}
      {localSelectedGroundStation && (
        <GroundStationMarker groundStation={localSelectedGroundStation} />
      )}
      
      {/* NORAD ID 기준으로 중복 제거 후 최대 3개 위성 표시 */}
      {(() => {
        // NORAD ID 기준으로 중복 제거 후 최대 3개 위성 표시
        const uniqueSatellites = (() => {
          const seen = new Set();
          const result: any[] = [];
          
          for (const satellite of latestSpacecrafts || []) {
            if (result.length >= 3) break; // 3개면 중단
            
            if (!seen.has(satellite.noradId)) {
              seen.add(satellite.noradId);
              result.push(satellite);
            }
          }
          
          // 디버깅 로그
          console.log('위성 데이터:', {
            total: latestSpacecrafts?.length || 0,
            unique: result.length,
            uniqueSatellites: result.map(s => ({ name: s.name, noradId: s.noradId }))
          });
          
          return result;
        })();
        
        return (
          <>
            {/* 위성 마커들 */}
            <SatelliteMarkers 
              latestSpacecrafts={uniqueSatellites}
              onSatelliteClick={handleSatelliteClick}
            />
            
            {/* 위성 궤도 라인 */}
            <SatelliteOrbitLines latestSpacecrafts={uniqueSatellites} />
          </>
        );
      })()}
      
             {/* 위성 스케줄 모달 */}
       <SatelliteModal
         selectedSatellite={selectedSatellite}
         isLoading={isLoading}
         scheduleData={scheduleData}
         onClose={() => setSelectedSatellite(null)}
       />
       

      </>
  );
};

// 메인 3D 시각화 컴포넌트
interface EarthAndSatelliteSceneProps {
  latestSpacecrafts?: any[];
  onGroundStationSelect?: (stationName: string) => void;
  groundStations?: any[];
  selectedGroundStation?: string | number;
}

const EarthAndSatelliteScene = ({ 
  latestSpacecrafts, 
  onGroundStationSelect,
  groundStations,
  selectedGroundStation
}: EarthAndSatelliteSceneProps) => {
  return (
    <EarthGroup 
      latestSpacecrafts={latestSpacecrafts} 
      onGroundStationSelect={onGroundStationSelect}
      groundStations={groundStations}
      selectedGroundStation={selectedGroundStation}
    />
  );
};

export default EarthAndSatelliteScene;